import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, ShieldOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
const LIBELLES = {
  rejet: ['Prélèvement rejeté', 'text-rose-700'],
  representation: ['Représentation programmée', 'text-violet-700'],
  regularisation: ['Échéance régularisée', 'text-emerald-700'],
  frais: ['Frais de rejet payés', 'text-amber-700'],
  frais_du: ['Frais de rejet dus', 'text-amber-700'],
  suspension_activee: ['Couverture suspendue', 'text-rose-800 font-bold'],
  suspension_levee_auto: ['Suspension levée (régularisation)', 'text-emerald-800 font-semibold'],
  suspension_levee_manuelle: ['Suspension levée manuellement', 'text-emerald-800 font-semibold'],
};

export const leverSuspension = async (mandatId, headers, apres) => {
  const motif = window.prompt('Motif de la levée de suspension (accord trouvé avec le membre) :');
  if (!motif || motif.trim().length < 3) return;
  try {
    await axios.post(`${API}/sepa-stripe/admin/mandats/${mandatId}/lever-suspension`, { motif: motif.trim() }, { headers });
    toast.success('Suspension levée — la couverture est de nouveau affichée valide');
    apres();
  } catch (e) { toast.error(e.response?.data?.detail || 'Levée impossible'); }
};

const detail = (e) => {
  if (e.type === 'rejet') return `échéance ${e.echeance} · ${eur(e.montant)}${e.tentative > 1 ? ` · tentative ${e.tentative}` : ''}${e.motif ? ` · ${e.motif}` : ''}`;
  if (e.type === 'representation') return `échéance ${e.echeance} · ${eur(e.montant)} · le ${fr(e.date_representation)}`;
  if (e.type === 'regularisation') return `échéance ${e.echeance} · ${eur(e.montant)} · ${e.mode}`;
  if (e.type === 'frais' || e.type === 'frais_du') return `${eur(e.montant)}${e.mode ? ` · ${e.mode}` : ''}`;
  return [e.motif, e.par && `par ${e.par}`].filter(Boolean).join(' · ');
};

/** Historique des rejets, frais et suspensions. `membreId` = vue administration (fiche membre) ; sinon espace membre. */
export const HistoriqueMandats = ({ membreId, headers }) => {
  const [data, setData] = useState(null);
  const auth = headers?.Authorization;
  const url = membreId ? `${API}/sepa-stripe/admin/membres/${membreId}/historique-mandats` : `${API}/sepa-stripe/membre/historique`;
  const charger = useCallback(() => {
    axios.get(url, { headers: { Authorization: auth } }).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [url, auth]);
  useEffect(() => { charger(); }, [charger]);
  if (!data || (!data.evenements.length && !data.suspendus.length)) return null;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white" data-testid={`historique-mandats-${membreId || 'membre'}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs">
        <History className="h-3.5 w-3.5 text-[#0a3d62]" />
        <b className="text-[#0a3d62]">Historique des prélèvements SEPA</b>
        <span className="text-slate-500">{data.nb_rejets} rejet(s) · frais payés {eur(data.frais_payes)}</span>
        {data.suspendus.map((s) => (
          <span key={s.mandat_id} className="ml-auto inline-flex items-center gap-1.5">
            <span className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`historique-suspendu-${s.mandat_id}`}>Couverture suspendue depuis le {fr(s.depuis)}</span>
            {membreId && (
              <button type="button" onClick={() => leverSuspension(s.mandat_id, { Authorization: auth }, charger)} className="inline-flex items-center gap-1 rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-800 hover:bg-emerald-100" data-testid={`historique-lever-${s.mandat_id}`}>
                <ShieldOff className="h-3 w-3" /> Lever la suspension
              </button>
            )}
          </span>
        ))}
      </div>
      <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
        {data.evenements.map((e, i) => {
          const [lib, cls] = LIBELLES[e.type] || [e.type, ''];
          return (
            <li key={`${e.mandat_id}-${e.type}-${i}`} className="flex flex-wrap gap-2 px-3 py-1.5 text-xs" data-testid={`historique-evt-${i}`}>
              <span className="w-20 text-slate-500">{fr(e.at)}</span>
              <span className={`w-56 ${cls}`}>{lib}</span>
              <span className="text-slate-600">{detail(e)}</span>
              <span className="ml-auto font-mono text-[10px] text-slate-400">{e.rum}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
